import { closeBrackets, closeBracketsKeymap, completionKeymap } from "@codemirror/autocomplete";
import { defaultKeymap, indentWithTab, toggleComment } from "@codemirror/commands";
import { bracketMatching, foldGutter, foldKeymap, indentOnInput, syntaxHighlighting } from "@codemirror/language";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { Compartment, EditorSelection, EditorState, type Extension } from "@codemirror/state";
import {
  crosshairCursor,
  drawSelection,
  dropCursor,
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
  rectangularSelection,
} from "@codemirror/view";
import { classHighlighter } from "@lezer/highlight";
import { vim } from "@replit/codemirror-vim";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "../components/ui";
import { collab } from "../editor/collab";
import { find, findKeymap, restoreFind, type FindPanel } from "../editor/find";
import { figureFolder, imageInsertion } from "../editor/images";
import { codeHighlighting, codeLanguage } from "../editor/languages";
import { compileKeys, editorTheme, latex } from "../editor/latex";
import { readAloud, readMode, type ReadAloudPanel } from "../editor/readaloud";
import { references } from "../editor/refs";
import { review } from "../editor/review";
import { learnWord, misspeltAt, spellcheck, suggestFor } from "../editor/spell";
import { api, type DocPosition, type ProjectIndex, type Role, type User, type UserSettings } from "../lib/api";
import { toast } from "../lib/app";
import { FindBar } from "./FindBar";
import { ReadAloudBar, speechContext } from "./ReadAloudBar";
import type { DocHandle, ProjectSession } from "./session";

export interface EditorApi {
  view: EditorView | null;
  docId: string | null;
  /** Move the cursor to a line (and column), selecting `length` characters from there. */
  goTo: (line: number, column?: number, length?: number) => void;
  cursorLine: () => { line: number; column: number } | null;
}

interface Props {
  session: ProjectSession;
  docId: string;
  /** Project path of the file. */
  path: string;
  user: User;
  role: Role;
  settings: UserSettings;
  tracking: boolean;
  reviewOpen: boolean;
  getIndex: () => ProjectIndex | null;
  onCompile: () => void;
  apiRef: React.MutableRefObject<EditorApi>;
  onCursorLine?: (line: number) => void;
  onDocChanged?: () => void;
  /** After every update: typing, cursor moves, remote edits. */
  onViewUpdate?: () => void;
  onReady?: (view: EditorView) => void;
  onFocusReviewItem?: (id: string) => void;
  onRequestComment?: (from: number, to: number) => void;
  /** Ctrl/⌘+Alt+N / P: next or previous comment or change. */
  onReviewNav?: (dir: 1 | -1) => void;
  onOpenLocation?: (file: string, line: number) => void;
  /** The find bar's "search all files" button, with what's in its search box. */
  onSearchAll?: (text: string) => void;
  onSpellMenu?: (opts: {
    x: number;
    y: number;
    word: string;
    suggestions: string[];
    /** For a word spelt the way another region spells it: this language's spelling and its name. */
    regional?: { spelling: string; label: string };
    replace: (w: string) => void;
    learn: () => void;
  }) => void;
  pendingJump?: { line: number; column?: number; length?: number } | null;
  onJumpDone?: () => void;
  /** Where you were in this file last time; it opens at the same spot. */
  savedPosition?: () => DocPosition | null;
  /** Where you are now, as you scroll, move the cursor and edit. */
  onPosition?: (at: DocPosition) => void;
}

/** Visual mode (and KaTeX with it) loads the first time someone switches it on; after that it's applied without a flash of code. */
let visualModule: typeof import("../editor/visual") | null = null;

export function Editor(props: Props) {
  const { session, docId, user, role, settings, tracking, reviewOpen } = props;
  const host = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState<DocHandle | null>(null);
  const [findPanel, setFindPanel] = useState<FindPanel | null>(null);
  const [readPanel, setReadPanel] = useState<ReadAloudPanel | null>(null);
  const trackingRef = useRef(tracking);
  trackingRef.current = tracking;
  const propsRef = useRef(props);
  propsRef.current = props;

  const compartments = useRef({
    keymap: new Compartment(),
    wrap: new Compartment(),
    spell: new Compartment(),
    review: new Compartment(),
    readOnly: new Compartment(),
    font: new Compartment(),
    language: new Compartment(),
    visual: new Compartment(),
  }).current;

  // Open the doc and wait for its first sync.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const h = session.openDoc(docId);
    session.setCurrentDoc(docId);
    h.synced.then(() => {
      if (!cancelled) {
        setHandle(h);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      setHandle(null);
    };
  }, [session, docId]);

  const reviewExt = (h: DocHandle): Extension =>
    review({
      doc: h.ydoc,
      ytext: h.ytext,
      canComment: reviewOpen && role !== "viewer",
      onFocusItem: (id) => propsRef.current.onFocusReviewItem?.(id),
      onRequestComment: (from, to) => propsRef.current.onRequestComment?.(from, to),
      onNavigate: (dir) => propsRef.current.onReviewNav?.(dir),
    });
  const spellExt = () => {
    const lang = settings.spellLang ?? "en-AU";
    // Your own name isn't a misspelling, on a title page or anywhere else.
    const ownName = user.displayName.match(/\p{L}[\p{L}'’]*\p{L}/gu) ?? [];
    return spellcheck(lang === "off" ? null : lang, [...(settings.dictionary ?? []), ...ownName], () => propsRef.current.getIndex());
  };
  const fontExt = () => EditorView.theme({ "&": { fontSize: `${settings.fontSize ?? 14}px` } });
  const readOnlyExt = () => [EditorState.readOnly.of(role === "viewer"), EditorView.editable.of(role !== "viewer")];
  const wantsVisual = () => propsRef.current.settings.editorMode === "visual" && /\.tex$/i.test(propsRef.current.path);
  const visualExt = (): Extension =>
    visualModule && wantsVisual()
      ? visualModule.visual({
          fileUrl: (path) => `/api/projects/${session.projectId}/raw?path=${encodeURIComponent(path)}`,
          files: () => propsRef.current.getIndex()?.files ?? [],
        })
      : [];
  const languageExt = (): Extension => {
    const onCompile = () => propsRef.current.onCompile();
    const code = codeLanguage(propsRef.current.path);
    return code ? [codeHighlighting(code.support ?? null), compileKeys(onCompile)] : latex(propsRef.current.getIndex, onCompile);
  };

  // Build the editor once the doc is synced.
  useEffect(() => {
    if (!handle || !host.current) return;
    const mem = propsRef.current.savedPosition?.() ?? null;
    const text = handle.ytext.toString();
    const clamp = (n: number) => Math.max(0, Math.min(n, text.length));
    // The line at the top of the view and how far it's scrolled past that line's top. It's kept up to date as
    // you go, because by the time the editor is torn down (going to History, say) it's out of the page and reads 0.
    let at = { top: clamp(mem?.top ?? 0), offset: mem?.offset ?? 0 };
    const scrollBack = () => EditorView.scrollIntoView(at.top, { y: "start", yMargin: -at.offset });
    const record = (s: EditorState) => {
      const { anchor, head } = s.selection.main;
      propsRef.current.onPosition?.({ anchor, head, ...at });
    };
    const state = EditorState.create({
      doc: text,
      selection: mem ? EditorSelection.single(clamp(mem.anchor), clamp(mem.head)) : undefined,
      extensions: [
        compartments.keymap.of(settings.keymap === "vim" ? vim() : []),
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        foldGutter(),
        drawSelection(),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        indentOnInput(),
        syntaxHighlighting(classHighlighter),
        bracketMatching(),
        closeBrackets(),
        rectangularSelection(),
        crosshairCursor(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        find(setFindPanel),
        readAloud({
          onPanel: setReadPanel,
          mode: () => readMode(propsRef.current.path),
          context: () => speechContext(session.projectId, propsRef.current.getIndex(), propsRef.current.path, viewRef.current?.state.doc.toString() ?? ""),
        }),
        compartments.language.of(languageExt()),
        references({
          getIndex: () => propsRef.current.getIndex(),
          getPath: () => propsRef.current.path,
          onOpen: (file, line) => propsRef.current.onOpenLocation?.(file, line),
        }),
        keymap.of([
          ...closeBracketsKeymap,
          { key: "Mod-/", run: toggleComment },
          { key: "Mod-Alt-m", run: (v) => {
            const s = v.state.selection.main;
            if (s.empty) return false;
            propsRef.current.onRequestComment?.(s.from, s.to);
            return true;
          } },
          ...defaultKeymap,
          ...findKeymap,
          ...searchKeymap,
          ...foldKeymap,
          ...completionKeymap,
          indentWithTab,
        ]),
        collab({
          ytext: handle.ytext,
          awareness: handle.provider.awareness!,
          undoManager: handle.undoManager,
          user: { id: user.id, name: user.displayName },
          isTracking: () => trackingRef.current,
        }),
        imageInsertion({
          canEdit: () => propsRef.current.role !== "viewer",
          folder: () => figureFolder(propsRef.current.getIndex()?.files ?? []),
          upload: async (path, data) => {
            const r = await api.upload<{ path: string }>(`/api/projects/${session.projectId}/upload?path=${encodeURIComponent(path)}&rename=1`, data);
            return r.path;
          },
          onError: (message) => toast(message, "error"),
        }),
        compartments.review.of(reviewExt(handle)),
        compartments.spell.of(spellExt()),
        compartments.wrap.of(settings.lineWrap === false ? [] : EditorView.lineWrapping),
        compartments.readOnly.of(readOnlyExt()),
        compartments.font.of(fontExt()),
        compartments.visual.of(visualExt()),
        editorTheme,
        EditorView.updateListener.of((u) => {
          if (u.selectionSet || u.docChanged) {
            const line = u.state.doc.lineAt(u.state.selection.main.head).number;
            propsRef.current.onCursorLine?.(line);
          }
          if (u.docChanged) {
            at = { ...at, top: u.changes.mapPos(at.top, -1) };
            propsRef.current.onDocChanged?.();
          }
          if (u.selectionSet || u.docChanged) record(u.state);
          propsRef.current.onViewUpdate?.();
        }),
        EditorView.domEventHandlers({
          contextmenu(e, view) {
            const pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
            if (pos === null) return false;
            const hit = misspeltAt(view, pos);
            if (!hit || !propsRef.current.onSpellMenu) return false;
            e.preventDefault();
            suggestFor(view, hit.word).then((suggestions) =>
              propsRef.current.onSpellMenu?.({
                x: e.clientX,
                y: e.clientY,
                word: hit.word,
                suggestions,
                regional: hit.regional,
                replace: (w) => view.dispatch({ changes: { from: hit.from, to: hit.to, insert: w } }),
                learn: () => learnWord(view, hit.word),
              }),
            );
            return true;
          },
        }),
      ],
    });
    // A jump made straight after (e.g. opening the file someone else is in) replaces this scroll, so it wins.
    const view = new EditorView({ state, parent: host.current, scrollTo: mem ? scrollBack() : undefined });
    viewRef.current = view;
    restoreFind(view);
    if (!mem) view.focus();

    // Hiding the editor (the PDF-only layout, or the PDF on a phone) loses its scroll position, so it's put back
    // when the editor reappears; scrolls while it's hidden aren't where you were. A jump made while hidden wins.
    let hidden = !view.scrollDOM.clientHeight;
    let jumpedWhileHidden = false;
    const onScroll = () => {
      const box = view.scrollDOM.getBoundingClientRect();
      if (hidden || !box.height) return;
      const line = view.lineBlockAtHeight(box.top - view.documentTop);
      // Measured to the same place a restore scrolls to, so opening and closing a file again doesn't creep.
      const lineTop = view.coordsAtPos(line.from, 1)?.top ?? view.documentTop + line.top;
      at = { top: line.from, offset: Math.round(box.top - lineTop) };
      record(view.state);
    };
    view.scrollDOM.addEventListener("scroll", onScroll, { passive: true });
    const visibility = new ResizeObserver(() => {
      const was = hidden;
      hidden = !view.scrollDOM.clientHeight;
      if (!was || hidden) return;
      const head = view.state.selection.main.head;
      view.dispatch({ effects: jumpedWhileHidden ? EditorView.scrollIntoView(head, { y: "center" }) : scrollBack() });
      jumpedWhileHidden = false;
    });
    visibility.observe(view.scrollDOM);
    propsRef.current.onCursorLine?.(state.doc.lineAt(state.selection.main.head).number);
    propsRef.current.onReady?.(view);

    props.apiRef.current = {
      view,
      docId,
      goTo: (line, column, length) => {
        if (hidden) jumpedWhileHidden = true;
        const l = view.state.doc.line(Math.max(1, Math.min(line, view.state.doc.lines)));
        const pos = Math.min(l.to, l.from + Math.max(0, column ?? 0));
        const head = Math.min(view.state.doc.length, pos + Math.max(0, length ?? 0));
        view.dispatch({ selection: { anchor: pos, head }, effects: EditorView.scrollIntoView(pos, { y: "center" }) });
        view.focus();
      },
      cursorLine: () => {
        const head = view.state.selection.main.head;
        const l = view.state.doc.lineAt(head);
        return { line: l.number, column: head - l.from };
      },
    };

    return () => {
      visibility.disconnect();
      view.scrollDOM.removeEventListener("scroll", onScroll);
      view.destroy();
      viewRef.current = null;
      if (props.apiRef.current.view === view) props.apiRef.current = { view: null, docId: null, goTo: () => {}, cursorLine: () => null };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle]);

  // Code files load their language on first use, and again if a rename changes it.
  const code = codeLanguage(props.path);
  useEffect(() => {
    const apply = () => viewRef.current?.dispatch({ effects: compartments.language.reconfigure(languageExt()) });
    if (code && !code.support) code.load().then(apply, (e) => console.warn("[language]", e));
    else apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Live settings.
  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.keymap.reconfigure(settings.keymap === "vim" ? vim() : []) });
  }, [settings.keymap]);
  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.wrap.reconfigure(settings.lineWrap === false ? [] : EditorView.lineWrapping) });
  }, [settings.lineWrap]);
  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.spell.reconfigure(spellExt()) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.spellLang, (settings.dictionary ?? []).length]);
  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.font.reconfigure(fontExt()) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.fontSize]);
  useEffect(() => {
    if (handle) viewRef.current?.dispatch({ effects: compartments.review.reconfigure(reviewExt(handle)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewOpen, role, handle]);
  useEffect(() => {
    viewRef.current?.dispatch({ effects: compartments.readOnly.reconfigure(readOnlyExt()) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);
  useEffect(() => {
    const apply = () => viewRef.current?.dispatch({ effects: compartments.visual.reconfigure(visualExt()) });
    if (wantsVisual() && !visualModule) {
      import("../editor/visual").then((m) => {
        visualModule = m;
        apply();
      }, (e) => toast(`Couldn't load the visual editor: ${e instanceof Error ? e.message : e}`, "error"));
    } else apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.editorMode, props.path, handle]);

  // Jumps requested before the view existed (e.g. clicking a log entry for another file).
  useEffect(() => {
    if (!props.pendingJump || !viewRef.current || loading) return;
    props.apiRef.current.goTo(props.pendingJump.line, props.pendingJump.column, props.pendingJump.length);
    props.onJumpDone?.();
  }, [props.pendingJump, loading, props]);

  return (
    <div className="editor-host" ref={host}>
      {loading && (
        <div className="editor-empty">
          <Spinner size={20} />
        </div>
      )}
      {findPanel && createPortal(<FindBar panel={findPanel} onSearchAll={props.onSearchAll} />, findPanel.dom)}
      {readPanel && createPortal(<ReadAloudBar panel={readPanel} />, readPanel.dom)}
    </div>
  );
}
